import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { listClaims, listManufacturers, listModels } from '../../services/pocApi';
import useApiResource from '../../components/poc/useApiResource';
import ClaimTable from '../../components/poc/ClaimTable';
import {
    Disclaimer, EmptyState, ErrorNotice, Field, LoadingState, PageHeader, Panel, POC_LINKS, SubNav,
} from '../../components/poc/PocUi';
import { CLAIM_STATUSES, PATTERNS, statusLabel } from '../../components/poc/pocFormat';

export const PAGE_SIZE = 20;      // the API accepts 1-100

/**
 * Claim list with server-side filtering and pagination. Filters live in the URL so they survive reloads and links.
 * The model filter depends on the manufacturer filter: it lists that manufacturer's catalogue models.
 */
export default function PocClaimHistory() {
    const [params, setParams] = useSearchParams();
    const manufacturer = /^[A-Z]{3}$/.test(params.get('manufacturer') || '') ? params.get('manufacturer') : '';
    const model = manufacturer && PATTERNS.modelCode.test(params.get('model') || '') ? params.get('model') : '';
    const status = CLAIM_STATUSES.includes(params.get('status')) ? params.get('status') : '';
    const page = Math.max(0, Number.parseInt(params.get('page') || '0', 10) || 0);
    const offset = page * PAGE_SIZE;

    const claims = useApiResource(
        (signal) => listClaims({ manufacturerCode: manufacturer, modelCode: model, claimStatus: status, limit: PAGE_SIZE, offset }, signal),
        [manufacturer, model, status, offset],
    );
    const manufacturers = useApiResource((signal) => listManufacturers(signal), []);
    const names = manufacturers.data || [];
    const models = useApiResource(
        (signal) => (manufacturer ? listModels(manufacturer, signal) : Promise.resolve([])),
        [manufacturer],
    );
    const modelItems = models.data || [];

    const update = (changes) => {
        const next = { manufacturer, model, status, page: String(page), ...changes };
        setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v !== '' && v !== '0')));
    };

    const data = claims.data;
    const total = data?.total ?? 0;
    const items = data?.items ?? [];
    const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

    let body;
    if (!data) body = null;
    else if (items.length) {
        body = <ClaimTable items={items} manufacturers={names} caption="POC claims" />;
    } else if (total > 0 && offset >= total) {
        body = (
            <EmptyState>
                This page is beyond the end of the results.{' '}
                <button type="button" className="underline underline-offset-2" onClick={() => update({ page: '0' })}>Go to the first page</button>
            </EmptyState>
        );
    } else if (manufacturer || model || status) {
        body = <EmptyState>No claims match the selected filters.</EmptyState>;
    } else {
        body = <EmptyState>No POC claims have been submitted.</EmptyState>;
    }

    return (
        <div className="space-y-8 max-w-7xl mx-auto">
            <SubNav label="POC claim management" links={POC_LINKS} />
            <PageHeader title="POC Claim History" subtitle="Every submitted POC claim with its current decision. Open a claim to see its full decision history." />
            <Disclaimer />

            <Panel title="Filters">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-end">
                    <Field label="Manufacturer">
                        <select className="form-select" value={manufacturer} onChange={(e) => update({ manufacturer: e.target.value, model: '', page: '0' })}>
                            <option value="">All manufacturers</option>
                            {names.map((m) => <option key={m.manufacturer_code} value={m.manufacturer_code}>{m.manufacturer_name} ({m.manufacturer_code})</option>)}
                        </select>
                    </Field>
                    <Field label="Model" hint={manufacturer ? (models.error ? 'Models could not be loaded.' : undefined) : 'Select a manufacturer first.'}>
                        <select className="form-select" value={model} onChange={(e) => update({ model: e.target.value, page: '0' })}
                                disabled={!manufacturer || models.loading}>
                            <option value="">All models</option>
                            {/* A model from a link stays selectable even before (or without) the catalogue list. */}
                            {model && !modelItems.some((m) => m.model_code === model) && <option value={model}>{model}</option>}
                            {modelItems.map((m) => <option key={m.model_code} value={m.model_code}>{(m.model_name || '').trim() || m.model_code} ({m.model_code})</option>)}
                        </select>
                    </Field>
                    <Field label="Status">
                        <select className="form-select" value={status} onChange={(e) => update({ status: e.target.value, page: '0' })}>
                            <option value="">All statuses</option>
                            {CLAIM_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                        </select>
                    </Field>
                    <div className="flex gap-3 md:col-span-2">
                        <button type="button" className="btn btn-secondary" onClick={() => setParams({})} disabled={!manufacturer && !model && !status && page === 0}>
                            Reset Filters
                        </button>
                        <button type="button" className="btn btn-secondary gap-2" onClick={claims.reload} disabled={claims.loading}>
                            <RefreshCw className="w-4 h-4" aria-hidden="true" />Refresh
                        </button>
                    </div>
                </div>
            </Panel>

            <Panel title="Claims">
                {claims.loading && <LoadingState label="Loading claims…" />}
                <ErrorNotice error={claims.error} onRetry={claims.reload} />
                {!claims.loading && body}
                {!claims.loading && data && total > 0 && (
                    <nav aria-label="Pagination" className="flex flex-wrap items-center justify-between gap-4 mt-6 text-xs">
                        <p className="text-text-muted" aria-live="polite">
                            {items.length
                                ? `Showing ${offset + 1}–${offset + items.length} of ${total}`
                                : `${total} claims in total`}
                        </p>
                        <div className="flex gap-2">
                            <button type="button" className="btn btn-secondary !px-4 !py-2" disabled={page === 0} onClick={() => update({ page: String(page - 1) })}>
                                Previous
                            </button>
                            <button type="button" className="btn btn-secondary !px-4 !py-2" disabled={page >= lastPage} onClick={() => update({ page: String(page + 1) })}>
                                Next
                            </button>
                        </div>
                    </nav>
                )}
            </Panel>
        </div>
    );
}
